import * as Api from '@ViewModels';
import { Button } from '@WebComponents/Button';
import { RadioButton } from '@WebComponents/RadioButton';
import { css } from 'aphrodite';
import * as React from 'react';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { getDisplayNameWithEmailFirst } from '../../../../models/UiUtils';
import { invalidateAllContactFilterQueries, useContactMergeMutation } from '../../../../queries';
import { charmGray } from '../../../styles/colors';
import { XIcon } from '../../svgs/icons/XIcon';
import { styleSheet } from './styles';

interface IProps {
	selectedContacts: Api.IContact[];
	onFinish?: (targetContact?: Api.IContact | null, canceled?: boolean) => void;
}

export const MergeContactsWizard: React.FC<IProps> = ({ selectedContacts, onFinish }) => {
	// Get contacts from either source
	const availableContacts = React.useMemo(() => {
		if (selectedContacts) {
			return selectedContacts;
		}
		return [];
	}, [selectedContacts]);

	// State for component
	const [selectedContact, setSelectedContact] = React.useState<Api.IContact | null>(() =>
		availableContacts.length > 0 ? availableContacts[0] : null
	);
	const [isLoading, setIsLoading] = React.useState(false);
	const [isHidden, setIsHidden] = React.useState(false);

	// Hooks
	const errorMessages = useErrorMessages();

	// React Query mutation
	const contactMergeMutation = useContactMergeMutation({
		onSuccess: () => {
			if (onFinish && selectedContact) {
				onFinish(selectedContact, false);
			}
		},
		onError: error => {
			setIsLoading(false);
			setIsHidden(false);

			errorMessages.push({
				messages: [error.systemMessage],
			});
		},
	});

	// Handlers
	const handleSelectContact = (contact: Api.IContact) => () => {
		setSelectedContact(contact);
	};

	const executeMerge = async () => {
		if (!selectedContact) return;

		const contactIdsToMerge = availableContacts.filter(c => c.id !== selectedContact.id).map(c => c.id);

		setIsLoading(true);
		setIsHidden(true);

		await contactMergeMutation.mutateAsync({
			targetContactId: selectedContact.id,
			contactIdsToMerge,
		});
		invalidateAllContactFilterQueries();
	};

	const closeModal = () => {
		if (onFinish) {
			onFinish(null, true);
		}
	};

	return (
		<div>
			<div className={css(styleSheet.modalWindow)} onClick={e => e.stopPropagation()} />
			<div className={css(styleSheet.mergeContactsWizardOuterContainer)}>
				<div className={css(styleSheet.mergeContactsCloseButtonContainer)}>
					<button onClick={closeModal}>
						<XIcon color={charmGray} height={12} width={12} />
					</button>
				</div>
				{isLoading && <div className='lev-loading-spinner' />}
				<section className={isHidden ? 'hide' : 'mergeContacts-contentContainer'}>
					<h3 className={css(styleSheet.mergeContactsHeadline)}>Merge contacts</h3>
					<p className={css(styleSheet.mergeContactsSubHeadline)}>
						Please select the <strong>main</strong> email address for this contact
					</p>
					<ul className={css(styleSheet.mergeContactList)}>
						{availableContacts.map(contact => (
							<li key={contact.id}>
								<button className={css(styleSheet.mergeContactEntryContainer)} onClick={handleSelectContact(contact)}>
									<RadioButton checked={contact.id === selectedContact?.id} key={contact.id} readOnly />
									<span className='emailAddress'>{getDisplayNameWithEmailFirst(contact)}</span>
								</button>
							</li>
						))}
					</ul>
					<footer className={css(styleSheet.mergeContactsButtonContainer)}>
						<Button kind='primary' onClick={executeMerge} disabled={!selectedContact || contactMergeMutation.isPending}>
							Merge
						</Button>
						<Button kind='reverse' onClick={closeModal}>
							Cancel
						</Button>
					</footer>
				</section>
			</div>
		</div>
	);
};
